import React, { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderedHtml } from 'modules/editor';
import { cn } from 'lib/utils';
import { countWords, MIN_WORDS } from '../../constants';
import type { CandidateQuestion, IeltsWritingTask } from '../../types';
import { ResizableSplit } from './ResizableSplit';

interface Props {
  question: CandidateQuestion;
  value: string;
  onChange: (value: string) => void;
}

/**
 * IELTS Writing: the task on the left, the script on the right, with a live
 * word count. The divider between them can be dragged.
 */
export function WritingTask({ question, value, onChange }: Props) {
  const { t } = useTranslation();
  const id = useId();
  const answerRef = useRef<HTMLTextAreaElement | null>(null);
  const [tab, setTab] = useState<'task' | 'draft'>('task');
  const [draft, setDraft] = useDraft(question.id);
  const task: IeltsWritingTask = question.writingTask ?? 'TASK_2';
  const words = countWords(value);
  const minimum = MIN_WORDS[task];

  const taskContent = (
    <section aria-label={t('exam.writing.task_prompt')} className="min-h-0 flex-1 overflow-y-auto p-5 sm:px-8 sm:py-7">
      <p className="font-mono text-[11px] font-bold uppercase tracking-[0.14em] text-[#B42318]">
        {task === 'TASK_1' ? t('exam.writing.task1') : t('exam.writing.task2')}
      </p>
      <p className="mt-2 text-[13px] text-[#646D84]">
        {t('exam.writing.guidance', { minutes: task === 'TASK_1' ? 20 : 40, words: minimum })}
      </p>
      <div className="mt-4 text-base leading-[1.65] text-[#10182B] sm:text-[16.5px]">
        <RenderedHtml html={question.prompt || ''} />
      </div>
      {question.mediaUrl && (
        <img src={question.mediaUrl} alt={t('exam.writing.task_visual')} className="mt-4 max-w-full rounded-md border border-gray-200" />
      )}
    </section>
  );

  const draftContent = (
    <section aria-label={t('exam.writing.draft')} className="flex min-h-0 flex-1 flex-col gap-3 p-5 sm:px-8 sm:py-7">
      <p className="rounded-[14px] border border-dashed border-[#CBD2E3] bg-white px-4 py-3.5 text-[13px] leading-relaxed text-[#4B5468]">
        {t('exam.writing.draft_note')}
      </p>
      <label htmlFor={`${id}-draft`} className="sr-only">
        {t('exam.writing.draft')}
      </label>
      <textarea
        id={`${id}-draft`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        spellCheck={false}
        placeholder={t('exam.writing.draft_placeholder')}
        className="min-h-[160px] flex-1 resize-none rounded-[14px] border border-[#E6E9F2] bg-white p-4 text-[15px] leading-relaxed text-[#10182B] focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-[#10182B]"
      />
    </section>
  );

  const promptPane = (
    <div className="flex min-h-0 flex-1 flex-col bg-[#FBFAF7]">
      <div role="tablist" aria-label={t('exam.writing.task_prompt')} className="flex shrink-0 gap-1 border-b border-[#E6E9F2] px-5 pt-3 sm:px-7">
        {(['task', 'draft'] as const).map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            id={`${id}-tab-${item}`}
            aria-selected={tab === item}
            tabIndex={tab === item ? 0 : -1}
            onClick={() => setTab(item)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault();
                const next = item === 'task' ? 'draft' : 'task';
                setTab(next);
                document.getElementById(`${id}-tab-${next}`)?.focus();
              }
            }}
            className={cn(
              'min-h-11 rounded-t-[10px] px-3.5 py-2.5 text-[13.5px] font-bold focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-[-3px] focus-visible:outline-[#10182B]',
              tab === item ? 'bg-white text-[#10182B] shadow-[inset_0_-2px_0_#B42318]' : 'text-[#646D84]',
            )}
          >
            {t(item === 'task' ? 'exam.writing.tab_task' : 'exam.writing.tab_draft')}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-labelledby={`${id}-tab-${tab}`} className="flex min-h-0 flex-1 flex-col">
        {tab === 'task' ? taskContent : draftContent}
      </div>
    </div>
  );

  const answerPane = (
    <section aria-label={t('exam.writing.your_answer')} className="flex min-h-0 flex-1 flex-col bg-white">
      <div role="toolbar" aria-label={t('exam.writing.edit_tools')} className="flex items-center gap-1.5 overflow-x-auto border-b border-[#EEF1F8] px-5 py-2 sm:flex-wrap sm:overflow-visible sm:px-7 sm:py-2.5">
        <ToolButton label={t('exam.writing.cut')} onRun={() => runEdit(answerRef.current, 'cut')} />
        <ToolButton label={t('exam.writing.copy')} onRun={() => runEdit(answerRef.current, 'copy')} />
        <ToolButton label={t('exam.writing.paste')} onRun={() => void pasteInto(answerRef.current)} />
        <span aria-hidden className="mx-1 h-5 w-px bg-[#E6E9F2]" />
        <ToolButton label={t('exam.writing.undo')} onRun={() => runEdit(answerRef.current, 'undo')} />
        <ToolButton label={t('exam.writing.redo')} onRun={() => runEdit(answerRef.current, 'redo')} />
        <span className="ml-auto text-xs text-[#646D84] max-md:hidden">{t('exam.writing.spell_off')}</span>
      </div>
      <label htmlFor={id} className="sr-only">
        {t('exam.writing.your_answer')}
      </label>
      <textarea
        ref={answerRef}
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="sentences"
        aria-describedby={`${id}-count`}
        // Edge to edge, so the global focus outline (drawn outside) would be clipped: draw it inside.
        className="min-h-0 flex-1 resize-none p-5 font-serif text-[17px] leading-[1.85] text-[#10182B] focus-visible:!outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#10182B] sm:px-9 sm:py-6 sm:text-[17.5px]"
        placeholder={t('exam.writing.placeholder')}
      />
      <div
        id={`${id}-count`}
        className="flex items-center gap-x-3 border-t border-[#E6E9F2] bg-[#FBFCFE] px-5 py-2.5 sm:flex-wrap sm:gap-x-[18px] sm:gap-y-1 sm:px-9 sm:py-3"
        aria-live="polite"
      >
        <span className={cn('text-[15px] font-extrabold', words >= minimum ? 'text-green-700' : 'text-[#92400E]')}>
          {t('exam.writing.word_count', { count: words })}
        </span>
        <div className="h-[7px] min-w-8 flex-1 overflow-hidden rounded-full bg-[#EEF1F8] sm:w-48 sm:flex-none" aria-hidden>
          <div
            className={cn('h-full rounded-full', words >= minimum ? 'bg-green-600' : 'bg-[#E9A23B]')}
            style={{ width: `${Math.min(100, (words / minimum) * 100)}%` }}
          />
        </div>
        <span className="shrink-0 whitespace-nowrap text-[13px] text-[#646D84]">{t('exam.writing.minimum', { count: minimum })}</span>
      </div>
    </section>
  );

  return <ResizableSplit handleLabel={t('exam.resize_panes')} start={promptPane} end={answerPane} />;
}

function ToolButton({ label, onRun }: { label: string; onRun: () => void }) {
  return (
    <button
      type="button"
      // Keeps the selection and focus in the answer while the button is pressed.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onRun}
      className="h-11 shrink-0 rounded-[9px] border border-[#E6E9F2] bg-white px-3 text-[12.5px] sm:h-8 sm:px-2.5 font-semibold text-[#364055] transition hover:bg-[#F4F6FB] focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[#10182B]"
    >
      {label}
    </button>
  );
}

/** Cut, copy, undo and redo act on the answer's own selection and history, like the keyboard shortcuts. */
function runEdit(el: HTMLTextAreaElement | null, command: 'cut' | 'copy' | 'undo' | 'redo') {
  if (!el || typeof document.execCommand !== 'function') return;
  el.focus();
  document.execCommand(command);
}

/** Paste needs the browser's say-so to read the clipboard; without it nothing is pasted. */
async function pasteInto(el: HTMLTextAreaElement | null) {
  if (!el || typeof document.execCommand !== 'function' || !navigator.clipboard?.readText) return;
  try {
    const text = await navigator.clipboard.readText();
    el.focus();
    document.execCommand('insertText', false, text);
  } catch {
    // Clipboard access refused: the keyboard shortcut still works.
  }
}

/** The scratch notes of one task: kept for the tab session so a reload does not lose them, never sent or graded. */
function useDraft(questionId: string): [string, (value: string) => void] {
  const key = `examing.exam-draft.${questionId}`;
  const [draft, setDraftState] = useState(() => {
    try {
      return sessionStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  });
  const setDraft = (value: string) => {
    setDraftState(value);
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch {
      // The notes live on in memory.
    }
  };
  return [draft, setDraft];
}

export default WritingTask;
